import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_PAGINATION = {
    page: 1,
    limit: 15,
    total: 0,
    totalPages: 1,
};

export function usePaginatedTasks({
    token,
    fetchTasks,
    scope = 'active',
    assignedToMe = false,
    hydrateTask,
    refreshSignal = 0,
    pageSize = 15,
}) {
    const [items, setItems] = useState([]);
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const searchDebounceRef = useRef(null);

    const loadPage = useCallback(
        async (page = 1) => {
            if (!token || typeof fetchTasks !== 'function') {
                setItems([]);
                setPagination(DEFAULT_PAGINATION);
                return;
            }

            setLoading(true);
            setError('');

            try {
                const result = await fetchTasks(token, {
                    page,
                    limit: pageSize,
                    search: search.trim(),
                    scope,
                    statusFilter,
                    assignedToMe,
                });

                const rows = Array.isArray(result?.items) ? result.items : [];
                // Hydrate sequentially so encryption unlock cannot race N MetaMask prompts
                // if the shared key cache is still empty (deriveEncryptionKeyPair also locks).
                let hydrated = rows;
                if (hydrateTask) {
                    hydrated = [];
                    for (const task of rows) {
                        hydrated.push(await hydrateTask(task));
                    }
                }

                setItems(hydrated);
                setPagination(result?.pagination || DEFAULT_PAGINATION);
            } catch (loadError) {
                setError(
                    loadError?.response?.data?.message ||
                        loadError?.message ||
                        'Failed to load tasks',
                );
                setItems([]);
            } finally {
                setLoading(false);
            }
        },
        [
            token,
            fetchTasks,
            search,
            scope,
            statusFilter,
            assignedToMe,
            hydrateTask,
            pageSize,
        ],
    );

    useEffect(() => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        const delay = search.trim() ? 350 : 0;
        searchDebounceRef.current = setTimeout(() => {
            loadPage(1);
        }, delay);

        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, [loadPage, search, refreshSignal, statusFilter, scope, assignedToMe, token]);

    return {
        items,
        pagination,
        search,
        setSearch,
        statusFilter,
        setStatusFilter,
        loading,
        error,
        loadPage,
        refresh: () => loadPage(pagination.page || 1),
    };
}
